import { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, Modal, SearchInput, Skeleton, Typography, Button } from "@/components/atoms";
import { useGetAccessListQuery } from "@/redux/apis/assistants/assistantsApi";
import { useGetClientsQuery } from "@/redux/apis/client/clientApi";
import { GetClientsParams } from "@/redux/apis/client/types";
import { useAppSelector } from "@/redux/hooks";
import { Client } from "@/redux/slices/client/types";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { getAssetUrl, getInitials } from "@/utils";

/** A row in the picker grid. Kept minimal so a non-client "(You)" row can be pinned by an EA flow. */
export interface ClientPickerProfile {
  id: string;
  name: string;
  avatarSrc?: string | null;
  /**
   * The client's account uuid. Same as `id` for the operator source; for the assistant source `id`
   * is the access-link id, so this carries the uuid separately. Callers that scope an API call
   * (rather than route on the id) need this one — e.g. bulk booking's `account_uuid`.
   */
  accountId?: string;
}

export interface ClientPickerModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fires with the chosen profile's id — the client account id ("operator" source) or the
   * access-link id ("assistant" source). Callers reset the draft + navigate.
   */
  onSelect: (id: string, profile?: ClientPickerProfile) => void;
  /**
   * Profiles pinned above the client list (e.g. the EA's own "(You)" row). Operators
   * pass none — this seam exists so the EA / travel-manager flow can reuse the modal.
   */
  pinnedProfiles?: ClientPickerProfile[];
  /**
   * Provider whose clients populate the grid. Defaults to the active operator; the EA /
   * travel-manager flow (which has no operator context) can pass its own provider or rely
   * solely on `pinnedProfiles` — so the modal never hard-depends on an active operator.
   */
  providerId?: string;
  /**
   * Data source for the grid. "operator" (default) lists the active provider's booking-eligible
   * clients; "assistant" (DEV-1296) lists the logged-in EA's CONNECTED managed clients and emits
   * the access-link id from `onSelect` so callers can route on `:accessId`.
   */
  source?: "operator" | "assistant";
  /** Overrides the default heading — the bulk upload flow asks a different question. */
  title?: string;
  description?: string;
}

/** A client can hold a booking only while active — pending-invite and access-removed are excluded (DEV-1177 view-only parity). */
const isBookingEligible = (client: Client) => client.status === "active";

/**
 * "Select a Profile to Book a Trip" — a searchable, browsable grid of clients to book on behalf
 * of (Figma 11220-124406). Selecting one calls `onSelect` with the profile id. Two data sources
 * (see `source`): the operator's booking-eligible clients (Clients-page provider list endpoint,
 * active only) or the EA's CONNECTED managed clients (account-management access list, DEV-1296).
 */
export const ClientPickerModal = ({
  open,
  onClose,
  onSelect,
  pinnedProfiles = [],
  providerId,
  source = "operator",
  title,
  description,
}: ClientPickerModalProps) => {
  const isAssistant = source === "assistant";
  // Falls back to the active operator without throwing (unlike useOperator) so the EA reuse seam
  // can mount this modal outside operator context and drive it purely from pinnedProfiles.
  const activeOperator = useAppSelector(activeOperatorSelector);
  const operatorId = providerId ?? activeOperator?.id;
  const [search, setSearch] = useState<string>();

  const params: GetClientsParams = {
    provider_uuid: operatorId ?? "",
    account_status: ["active"],
    search,
    page_size: "100",
  };
  const { data, isFetching, isError } = useGetClientsQuery(params, { skip: !open || !operatorId || isAssistant });

  // Assistant source: the EA's CONNECTED managed clients. Fetched unfiltered and searched
  // client-side (mirrors MyClients) so typing never refetches; the profile id is the access-link
  // id (`link.id`), which the caller routes on — the client account uuid is resolved by the
  // scoped booking wrapper via useGetAccessQuery.
  const {
    data: accessLinks = [],
    isFetching: accessFetching,
    isError: accessError,
  } = useGetAccessListQuery({ type: "YOU_MANAGE", status: "CONNECTED" }, { skip: !open || !isAssistant });
  const query = search?.trim().toLowerCase() ?? "";
  const managedProfiles: ClientPickerProfile[] = accessLinks
    .filter((link) => !query || link.party.name?.toLowerCase().includes(query) || link.party.email?.toLowerCase().includes(query))
    .map((link) => ({
      id: link.id,
      // The list query already filters to CONNECTED, so this uuid is safe to scope calls with.
      accountId: link.party.uuid,
      name: link.party.name || link.party.email,
      // party.avatar is a relative CDN key, not a URL — resolve it through the image handler (as
      // ClientAvatar does) or the <img> 404s against the app origin. Null when the client has none.
      avatarSrc: link.party.avatar ? getAssetUrl(link.party.avatar, { height: 120, width: 120, fit: "cover" }) : null,
    }));

  const eligibleClients = (data?.data.clients ?? []).filter(isBookingEligible);
  const profiles: ClientPickerProfile[] = [
    ...pinnedProfiles,
    ...(isAssistant ? managedProfiles : eligibleClients.map((client) => ({ id: client.id, accountId: client.id, name: client.name }))),
  ];
  // The grid shows one page of up to 100. Server-side search still reaches every client, so when the
  // active-client count exceeds what's rendered, nudge the operator to search rather than scroll.
  // (Operator source only — the assistant list is complete and filtered client-side.)
  const totalActive = data?.data.page.total ?? 0;
  const isTruncated = !isAssistant && totalActive > eligibleClients.length;

  const isListFetching = isAssistant ? accessFetching : isFetching;
  const isListError = isAssistant ? accessError : isError;
  // The EA truly manages nobody (not a no-match search) — point them at My Clients to request access.
  const showAssistantEmptyState = isAssistant && !query && accessLinks.length === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-large"
      title={title ?? (isAssistant ? "Select a Client to Book a Trip" : "Select a Profile to Book a Trip")}
      description={description ?? "Choose a client to create a booking under it."}
      renderButtons={() => (
        <Button variant="tertiary" size="md" onClick={onClose}>
          Cancel
        </Button>
      )}
    >
      <div className="flex flex-col gap-4">
        <SearchInput search={search} onSearch={setSearch} placeholder="Search user" className="w-full [&>input]:w-full" />

        <div className="max-h-[45vh] overflow-y-auto">
          {isListFetching ? (
            <Skeleton />
          ) : isListError ? (
            <Typography variant="paragraph" className="py-8 text-center text-danger">
              Couldn&apos;t load your clients. Please try again.
            </Typography>
          ) : profiles.length ? (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {profiles.map((profile) => (
                  <ProfileCard key={profile.id} profile={profile} onSelect={onSelect} />
                ))}
              </div>
              {isTruncated && (
                <Typography variant="paragraph" className="text-center text-neutral-dark-gray">
                  Showing the first {eligibleClients.length} of {totalActive} clients. Search by name to find anyone not listed.
                </Typography>
              )}
            </div>
          ) : showAssistantEmptyState ? (
            <div className="flex flex-col items-center gap-1 py-8 text-center">
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                You aren&apos;t managing any clients yet.
              </Typography>
              <Link to="/client/my-clients" onClick={onClose}>
                <Typography variant="action" className="text-primary">
                  Go to My Clients
                </Typography>
              </Link>
            </div>
          ) : (
            <Typography variant="paragraph" className="py-8 text-center text-neutral-dark-gray">
              No clients found.
            </Typography>
          )}
        </div>
      </div>
    </Modal>
  );
};

interface ProfileCardProps {
  profile: ClientPickerProfile;
  onSelect: (id: string, profile?: ClientPickerProfile) => void;
}

const ProfileCard = ({ profile, onSelect }: ProfileCardProps) => (
  <button
    type="button"
    onClick={() => onSelect(profile.id, profile)}
    className="flex w-full items-center gap-3 rounded-lg border border-neutral-mid-gray bg-white p-2.5 text-left transition-colors hover:border-primary hover:bg-primary-light"
  >
    <Avatar size="md" src={profile.avatarSrc ?? undefined} alt={profile.name} className="bg-neutral-mid-gray">
      {!profile.avatarSrc && (
        <Typography variant="action" className="text-neutral-dark-gray">
          {getInitials(profile.name)}
        </Typography>
      )}
    </Avatar>
    <Typography variant="action" className="min-w-0 truncate text-neutral-black">
      {profile.name}
    </Typography>
  </button>
);
